import { makeAutoObservable } from 'mobx';
import { CreateToggleRequest, PagedFeatureToggles, FeatureToggle, Toggle, TogglesService } from 'services/featuregating/generated';
import { Loadable } from './common';

export type { PagedFeatureToggles, FeatureToggle }

export interface IToggleStore
{
  pagedFeatureToggles: Loadable<PagedFeatureToggles>,
  toggle: Loadable<Toggle>,

  findAsync: (query?: string, page?: number, pageSize?: number) => Promise<void>,
  requestScopeSuggestionsAsync: (query?: string, limit?: number) => Promise<string[]>,
  createAsync: (feature_id: number, path: string, is_enabled: boolean) => Promise<void>,
  deleteAsync: (id: number) => Promise<void>,
}

export class ToggleStore implements IToggleStore
{
  pagedFeatureToggles: Loadable<PagedFeatureToggles> = new Loadable<PagedFeatureToggles>();
  toggle: Loadable<Toggle> = new Loadable<Toggle>();
  scopeSuggestions: Loadable<string[]> = new Loadable<string[]>();

  constructor()
  {
    makeAutoObservable(this);
  }

  async findAsync(query: string = "", page: number = 0, pageSize: number = 30)
  {
    await this.usingLoadingAsync
    (
      this.pagedFeatureToggles,
      async () =>
      {
        const pagedFeatureToggles = await TogglesService.getToggles({query: query, page: `${page}`, pageSize: `${pageSize}`});
        this.setPagedFeatureToggles(pagedFeatureToggles);
      },
    );
  }
  async requestScopeSuggestionsAsync(query: string = "", limit: number = 30)
  {
    try
    {
      const pagedPaths = await TogglesService.getTogglePaths({query: query, page: '0', pageSize: `${limit}`});
      return pagedPaths.data || [];
    }
    catch (e)
    {
      return [];
    }
  }

  async createAsync(feature_id: number, path: string, is_enabled: boolean)
  {
    const normalizedPath = '/' + `${path}`.replace(/\\/g, '/').replace(/(^\/+|\/+$)/g, '');
    const request: CreateToggleRequest =
    {
      feature_id: feature_id,
      is_enabled: is_enabled,
      path: normalizedPath,
    };
    await this.usingLoadingAsync
    (
      this.toggle,
      async () =>
      {
        const toggle = await TogglesService.postToggles({requestBody: request});
        this.setToggle(toggle);
        await this.pagedFeatureToggles.refreshAsync();
      },
    );
  }

  async deleteAsync(id: number)
  {
    await this.usingLoadingAsync
    (
      this.toggle,
      async () =>
      {
        await TogglesService.deleteToggleById({id: id});
        this.setToggle();
        await this.pagedFeatureToggles.refreshAsync();
      },
    );
  }

  private async usingLoadingAsync<T>(loadable: Loadable<T>, actionAsync: () => Promise<void>, errorHandler?: <T extends Error>(e: T) => void)
  {
    loadable.clearError();
    loadable.setIsLoading(true);
    try
    {
      await actionAsync();
      loadable.dataRefreshOperator = async () => await actionAsync();
    }
    catch (e)
    {
      const error = e as Error;
      if (errorHandler)
      {
        errorHandler(error);
      }
      else
      {
        loadable.setError(error);
      }
    }
    finally
    {
      loadable.setIsLoading(false);
    }
  }
  private setPagedFeatureToggles(pagedFeatureToggles?: PagedFeatureToggles): void
  {
    this.pagedFeatureToggles.data = pagedFeatureToggles;
  }
  private setToggle(toggle?: Toggle): void
  {
    this.toggle.data = toggle;
  }
}